import { useState, useEffect, useRef } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { Btn, Modal } from '../ui';

// Admin "Run research on all deals". The user picks a scope, sees the count, and
// the batch is driven CLIENT-SIDE over the reliable per-deal inline run endpoint
// (POST /deals/{id}/research/run) with bounded concurrency — so it doesn't depend
// on the flaky storage-queue worker. Keep the tab open while it runs.

const SCOPES = [
  { key: 'none',   label: 'Only deals with no research yet', hint: 'Fills the gaps — cheapest' },
  { key: 'active', label: 'Active deals',                    hint: 'The live pipeline' },
  { key: 'all',    label: 'Every deal',                      hint: 'Re-runs deals that already have research — most expensive' },
];
const CONCURRENCY = 3;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const BulkResearchModal = ({ onClose }) => {
  const [scope, setScope]                 = useState('none');
  const [targets, setTargets]             = useState(null);   // { count, deals }
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [err, setErr]                     = useState(null);
  const [phase, setPhase]                 = useState('idle'); // idle | running | done
  const [progress, setProgress]           = useState({ done: 0, ok: 0, failed: 0, total: 0 });
  const stopRef = useRef(false);

  // (Re)load the target count whenever the scope changes, while idle.
  useEffect(() => {
    if (phase !== 'idle') return undefined;
    let cancelled = false;
    setLoadingTargets(true); setErr(null);
    api.get(`deals/research/targets?scope=${scope}`)
      .then((d) => { if (!cancelled) setTargets(d); })
      .catch((e) => { if (!cancelled) setErr(e.message || 'Failed to load deals'); })
      .finally(() => { if (!cancelled) setLoadingTargets(false); });
    return () => { cancelled = true; };
  }, [scope, phase]);

  // Poll one deal's research status until it leaves 'pending' (or we give up).
  const pollDeal = async (dealId, maxMs = 6 * 60 * 1000) => {
    const start = Date.now();
    while (Date.now() - start < maxMs && !stopRef.current) {
      await sleep(5000);
      try {
        const r = await api.get(`deals/${dealId}/research`);
        const st = r?.latest?.status;
        if (st && st !== 'pending') return st;
      } catch { /* transient — keep polling */ }
    }
    return 'timeout';
  };

  const runBatch = async () => {
    const deals = (targets?.deals || []).slice();
    if (!deals.length) return;
    stopRef.current = false;
    setPhase('running');
    setProgress({ done: 0, ok: 0, failed: 0, total: deals.length });

    let idx = 0;
    const worker = async () => {
      // `idx++` is synchronous before any await, so no two workers take the same deal.
      while (!stopRef.current) {
        const deal = deals[idx++];
        if (!deal) return;
        let ok = false;
        try {
          api.post(`deals/${deal.dealId}/research/run`, {}).catch(() => {}); // fire; it runs inline server-side
          ok = (await pollDeal(deal.dealId)) === 'ready';
        } catch { ok = false; }
        setProgress((p) => ({ done: p.done + 1, ok: p.ok + (ok ? 1 : 0), failed: p.failed + (ok ? 0 : 1), total: p.total }));
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, deals.length) }, worker));
    setPhase('done');
  };

  const guardedClose = () => { if (phase !== 'running') onClose(); };
  const count = targets?.count ?? 0;
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <Modal title="Run research on all deals" onClose={guardedClose} width={520}>
      <div style={{ padding: 4 }}>
        {phase === 'idle' && (
          <>
            <div style={{ fontSize: 13, color: B.textMd, marginBottom: 12, lineHeight: 1.5 }}>
              Runs the full research (multi-agent web search + document reading + brief) on each selected deal.
              This takes a few minutes per deal and uses AI credits — <strong>keep this tab open</strong> while it runs.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {SCOPES.map((s) => (
                <label key={s.key} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', cursor: 'pointer',
                  border: `1px solid ${scope === s.key ? B.navy : B.border}`, borderRadius: 8,
                  background: scope === s.key ? `${B.navy}0d` : B.surface,
                }}>
                  <input type="radio" name="bulk-scope" checked={scope === s.key} onChange={() => setScope(s.key)} style={{ marginTop: 3 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: B.textLt }}>{s.hint}</div>
                  </div>
                </label>
              ))}
            </div>
            {err && <div style={{ color: B.red, fontSize: 12, marginBottom: 10 }}>{err}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: B.textMd }}>
                {loadingTargets ? 'Counting…' : `${count} ${count === 1 ? 'deal' : 'deals'} will run`}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                <Btn disabled={loadingTargets || !count} onClick={runBatch}>Run on {count}</Btn>
              </div>
            </div>
          </>
        )}

        {phase !== 'idle' && (
          <>
            <div style={{ fontSize: 13, color: B.textMd, marginBottom: 10 }}>
              {phase === 'running' ? 'Running research…' : 'Finished.'}{' '}
              <strong>{progress.done} / {progress.total}</strong> · {progress.ok} ok · {progress.failed} failed
            </div>
            <div style={{ height: 8, background: B.surfaceMuted, borderRadius: 4, overflow: 'hidden', marginBottom: 14 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: B.green, transition: 'width 0.3s' }} />
            </div>
            <div style={{ fontSize: 12, color: B.textLt, marginBottom: 14, lineHeight: 1.5 }}>
              {phase === 'running'
                ? 'Keep this tab open. You can stop at any time — deals already in progress will finish.'
                : `${progress.ok} succeeded, ${progress.failed} did not complete. Re-run any that failed from their Research tab.`}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {phase === 'running'
                ? <Btn variant="danger" onClick={() => { stopRef.current = true; }}>Stop</Btn>
                : <Btn onClick={onClose}>Close</Btn>}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
